import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography, Stack, Table, TableHead, TableRow, TableCell, TableBody, TextField, Button, CircularProgress } from '@mui/material';
import styles from '../styles';
import apis from '../../../apis';
import { AppContext } from '../../../context';
import { useToastContext } from '../../../components/Toast/toastContext';
import { IRetentionConfig } from '../../../resource';

const MODULES = [
  { label: 'Document', key: 'document' },
  { label: 'Users', key: 'user' },
  { label: 'Customer Documents', key: 'customerDocument' },
  { label: 'Template', key: 'template' },
  { label: 'ESG', key: 'esg' },
  { label: 'Widget', key: 'widget' },
];

const DEFAULT_DAYS = 30;

const RetentionSettings: React.FC = () => {
  const { account } = useContext(AppContext);
  const { activeToast } = useToastContext();
  const [retentionDays, setRetentionDays] = useState<IRetentionConfig>({
    document: DEFAULT_DAYS,
    user: DEFAULT_DAYS,
    customerDocument: DEFAULT_DAYS,
    template: DEFAULT_DAYS,
    esg: DEFAULT_DAYS,
    widget: DEFAULT_DAYS,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!account?.id) return;
    setLoading(true);
    apis.user.getRetentionConfig(account.id)
      .then(res => {
        if (res.success && res.retentionConfig) {
          setRetentionDays(res.retentionConfig);
        }
      })
      .catch(err => {
        activeToast({ type: 'error', text: err.msg || 'Failed to load retention config.' });
      })
      .finally(() => setLoading(false));
  }, [account?.id]);

  const handleChange = (key: keyof IRetentionConfig, value: string) => {
    const days = Math.max(1, parseInt(value, 10) || DEFAULT_DAYS);
    setRetentionDays(prev => ({ ...prev, [key]: days }));
  };

  const handleSave = () => {
    if (!account?.id) return;
    setSaving(true);
    apis.user.updateRetentionConfig(account.id, retentionDays)
      .then(res => {
        if (res.success) {
          setRetentionDays(res.retentionConfig);
          activeToast({ type: 'success', text: 'Settings saved!' });
        } else {
          activeToast({ type: 'error', text: 'Failed to save settings.' });
        }
      })
      .catch(err => {
        activeToast({ type: 'error', text: err.msg || 'Failed to save settings.' });
      })
      .finally(() => setSaving(false));
  };

  return (
    <Box>
      <Typography variant="h6" sx={styles.title}>Soft-Delete Retention Settings</Typography>
      <Typography sx={styles.description}>
        Configure how many days soft-deleted data will remain in the database before permanent deletion. Default is 30 days per module.
      </Typography>
      {loading ? (
        <Stack alignItems="center" justifyContent="center" minHeight={200}>
          <CircularProgress />
        </Stack>
      ) : (
        <Box component="form" sx={styles.form} onSubmit={e => { e.preventDefault(); handleSave(); }}>
          <Table sx={styles.table}>
            <TableHead>
              <TableRow>
                <TableCell sx={styles.th}>Module</TableCell>
                <TableCell sx={styles.th}>Retention Days</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {MODULES.map(({ label, key }) => (
                <TableRow sx={styles.tr} key={key}>
                  <TableCell sx={styles.td}>{label}</TableCell>
                  <TableCell sx={styles.td}>
                    <TextField
                      type="number"
                      size="small"
                      inputProps={{ min: 1 }}
                      value={retentionDays[key]}
                      onChange={e => handleChange(key as keyof IRetentionConfig, e.target.value)}
                      sx={styles.input}
                      disabled={saving}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Stack direction="row" justifyContent="flex-end">
            <Button type="submit" variant="contained" sx={styles.saveButton} disabled={saving}>
              {saving ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default RetentionSettings;
